import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { ENDPOINTS, SITE } from "@/config/site";

const schema = z.object({
  name: z.string().min(2, "Merci d'indiquer votre nom."),
  email: z.string().email("Adresse email invalide."),
  subject: z.string().min(2, "Merci d'indiquer un sujet."),
  message: z.string().min(10, "Votre message doit contenir au moins 10 caractères."),
  rgpd: z.literal(true, { errorMap: () => ({ message: "Votre consentement est nécessaire." }) }),
  website: z.string().max(0).optional(),
});

export const ContactForm = () => {
  const navigate = useNavigate();
  const ts = useMemo(() => Date.now(), []);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema), defaultValues: { website: "" } });

  const onSubmit = async (data) => {
    try {
      const res = await fetch(ENDPOINTS.contact, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ts: String(ts) }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.detail || json.error || "Erreur d'envoi");
      toast.success("Votre message a bien été envoyé. Nous vous répondons sous 48 heures ouvrées.");
      navigate("/merci", { state: { type: "contact", name: data.name } });
    } catch (err) {
      toast.error(`L'envoi a échoué (${err.message}). Appelez-nous au ${SITE.phone.display} ou écrivez à ${SITE.email}.`);
    }
  };

  const cls = (k) => `field ${errors[k] ? "field-error" : ""}`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5" data-testid="contact-form">
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register("website")} />
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label htmlFor="c-name" className="label">Nom <span className="text-copper">*</span></label><input id="c-name" {...register("name")} className={cls("name")} data-testid="contact-name" />{errors.name && <p className="error-text" role="alert" data-testid="error-contact-name">{errors.name.message}</p>}</div>
        <div><label htmlFor="c-email" className="label">Email <span className="text-copper">*</span></label><input id="c-email" type="email" {...register("email")} className={cls("email")} data-testid="contact-email" />{errors.email && <p className="error-text" role="alert" data-testid="error-contact-email">{errors.email.message}</p>}</div>
      </div>
      <div><label htmlFor="c-subject" className="label">Sujet <span className="text-copper">*</span></label><input id="c-subject" {...register("subject")} className={cls("subject")} data-testid="contact-subject" />{errors.subject && <p className="error-text" role="alert" data-testid="error-contact-subject">{errors.subject.message}</p>}</div>
      <div><label htmlFor="c-message" className="label">Message <span className="text-copper">*</span></label><textarea id="c-message" rows={5} {...register("message")} className={cls("message")} data-testid="contact-message" />{errors.message && <p className="error-text" role="alert" data-testid="error-contact-message">{errors.message.message}</p>}</div>
      <label className="flex items-start gap-3 text-sm text-ink/85">
        <input type="checkbox" {...register("rgpd")} className="mt-1 w-4 h-4 accent-copper" data-testid="contact-rgpd" />
        <span>J'accepte que mes données soient traitées par O'RIZON 360 pour répondre à ma demande, conformément à la <Link to="/politique-de-confidentialite" className="text-copper underline underline-offset-2">politique de confidentialité</Link>. <span className="text-copper">*</span></span>
      </label>
      {errors.rgpd && <p className="error-text" role="alert" data-testid="error-contact-rgpd">{errors.rgpd.message}</p>}
      <button type="submit" disabled={isSubmitting} className="btn-dark w-full sm:w-auto disabled:opacity-60" data-testid="contact-submit-button">
        {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Envoyer le message
      </button>
    </form>
  );
};
